import { useMemo } from "react";
import { TASK_TYPE_ID } from "./useTaskTypeFilter";
import { hfbXxxStringTranslator, useTranslation } from "@coworker/locales";
import { SelectItem } from "../../shared/filters/types/filter";

const NO_OPTIONS: SelectItem[] = [];

const getDepartmenOptionTitle = (
  t: (key: string, options?: any) => string,
  department: string
) => {
  if (department.match(/[0-9][0-9]/)) {
    return `HFB ${department} - ${hfbXxxStringTranslator(department, t)}`;
  } else {
    let translationString = "";
    switch (department) {
      case "AsIs":
      case "ClickAndCollect":
      case "Bistro":
      case "Checkout":
      case "CustomerServiceArea":
      case "EntranceInclGreeter":
      case "FinancingArea":
      case "FirstRoom":
      case "FullServeHandouts":
      case "HomeServicePointPlanningStudio":
      case "IKEAForBusiness":
      case "IKEARestaurant":
      case "IngkaCenters":
      case "OpenTheWalletMarkethall":
      case "OpenTheWalletShowroom":
      case "ParkingZone":
      case "PickUpPointHub":
      case "QuestionsFromCoWorkers":
      case "Restaurant":
      case "SelfServe":
      case "Smaland":
      case "SwedishFoodMarket":
        translationString = `otherArea${department}String`;
        break;

      case "whole":
        translationString = "wholeStoreString";
        break;

      default:
        return hfbXxxStringTranslator(department, t);
    }
    return t(translationString);
  }
};

const buildMultiSelectFilterOptions = (
  t: (key: string, options?: any) => string,
  departments: string[]
) => {
  const options: SelectItem[] = departments.map((department) => {
    return {
      title: getDepartmenOptionTitle(t, department),
      value: department,
      dependsOnFilterId: TASK_TYPE_ID,
    };
  });
  return options;
};

const buildGroupedMultiSelectFilterOptions = (
  t: (key: string, options?: any) => string,
  hfbDepartments: string[],
  otherDepartments: string[]
) => {
  const options: SelectItem[] = [
    {
      title: t(`hfbString`),
      options: hfbDepartments.map((department) => ({
        title: getDepartmenOptionTitle(t, department),
        value: department,
        dependsOnFilterId: TASK_TYPE_ID,
      })),
    },
    {
      title: t(`otherString`),
      options: otherDepartments.map((department) => ({
        title: getDepartmenOptionTitle(t, department),
        value: department,
        dependsOnFilterId: TASK_TYPE_ID,
      })),
    },
  ];
  return options;
};

const buildDepartmentFilterOptions = (
  t: (key: string, options?: any) => string,
  taskTypeValueValue: string,
  departments: string[]
) => {
  const hfbDepartments = departments
    .filter((department) => department.match(/[0-9][0-9]/))
    .sort();
  const otherDepartments = departments
    .filter((department) => !department.match(/[0-9][0-9]/))
    .sort((str1, str2) => (str2 === "OTHER" ? -1 : str1.localeCompare(str2)));

  if (taskTypeValueValue === "mfaq") {
    return buildGroupedMultiSelectFilterOptions(
      t,
      hfbDepartments,
      otherDepartments
    );
  } else {
    return buildMultiSelectFilterOptions(t, [
      ...hfbDepartments,
      ...otherDepartments,
    ]);
  }
};

export function useLocationsOptions(
  taskTypeValueValue: string,
  taskLocations: string[]
): SelectItem[] {
  const { t } = useTranslation();
  return useMemo(() => {
    if (!taskLocations) return NO_OPTIONS;

    return buildDepartmentFilterOptions(t, taskTypeValueValue, taskLocations);
  }, [taskLocations, taskTypeValueValue, t]);
}
